(ns rothko.audio.wave-player.component
  (:require [clojure.set :as set]
            [goog.events :as gevents]
            [medley.core :as medley]
            [reagent.core :as reagent]
            [rothko.button.component :refer [button separator]]
            [rothko.common :as common]
            [rothko.component.core :refer [defcomp]]
            [rothko.dom.event :as event]
            [rothko.icon.component :refer [icon]]
            [rothko.icon-animated.component :refer [spinner]]
            [rothko.layout.component :refer [layout]]
            [rothko.panel.component :refer [panel]]
            [utility-belt.pure :refer [deep-merge]]
            [goog.functions]
            [wavesurfer.js :as Wavesurfer]
            ["/js/wavesurfer.cursor-time" :default wavesurfer-cursor-time-plugin]
            ["wavesurfer.js/dist/plugin/wavesurfer.regions.js" :as wavesurfer-regions-plugin]
            [clojure.string :as string]))

(def container-id "wavesurfer")

(def region-default-rgb "170 170 170")

(def region-selected->rgb-alpha {true 0.75 false 0.30})

(defn rgb->css-colour
  ([rgb]
   (str "rgb(" rgb ")"))
  ([rgb alpha]
   (str "rgb(" rgb " / " alpha ")")))

(def wavesurfer-defaults {:autoCenter true
                          :barHeight 2
                          :height 128
                          :barWidth 1
                          :container (str "#" container-id)
                          :cursorWidth 2
                          :fillParent true
                          :responsive true
                          :scrollParent true})

(def wavesurfer-cursor-time-defaults {:hideOnBlur true
                                      :hideOnScroll false
                                      :customStyle {}})

(def just-added-region-defaults {:data {:just-added? true} ; This region has just been added by a user, as opposed to loaded or restored using undo.
                                 :color (rgb->css-colour region-default-rgb (region-selected->rgb-alpha true))})

(def new-region-span-sec 0.5)

(defn time-marker-span-sec
  [zoom-level]
  (/ 7 zoom-level)) ; The span has to be represented in seconds, and we want a constant pixel width regardless of the zoom level.

(def initial-state {:loading-progress 0
                    :options {:cursor-time wavesurfer-cursor-time-defaults
                              :regions {}
                              :wavesurfer wavesurfer-defaults}
                    :ready? false
                    :wavesurfer nil
                    :zoom-level 20
                    :playhead-position-initialised? false
                    :regions/colour-id->rgb {}
                    :regions/colour-id-key nil
                    :regions/time-marker-types {}
                    :regions/live {}
                    :regions/stable {} ; Stable regions are the same as live regions except when a user is in the middle of updating a region with a mouse.
                    :regions/undos []
                    :regions/redos []
                    :regions/selected-id nil
                    :regions/selected-is-locked? true
                    :regions/last-id-to-select nil
                    :regions/initialised-from-props? false})

(defn add-plugins
  [plugins options]
  (doall
   (for [plugin plugins]
     (case plugin
       :cursor-time (.create ^js wavesurfer-cursor-time-plugin (clj->js (:cursor-time options)))
       :regions (.create ^js wavesurfer-regions-plugin (clj->js (:regions options)))
       nil))))

(defn make-container-id
  [id]
  (str container-id "-" id))

(defn create-wavesurfer
  [state plugins id]
  (let [wavesurfer-options (:options @state)
        plugins (add-plugins plugins wavesurfer-options)]
    (->> (merge
          (:wavesurfer wavesurfer-options)
          {:container (str "#" (make-container-id id))}
          (when (seq plugins) {:plugins plugins}))
         (clj->js)
         (new Wavesurfer))))

(defn wavesurfer-loaded?
  [state]
  (:loaded? @state))

(defn regions-plugin?
  [plugins]
  (some #(= % :regions) plugins))

(defn get-wavesurfer
  [state]
  (:wavesurfer @state))

(defn set-options!
  [state options]
  (swap! state assoc :options (deep-merge (:options @state) options)))

(defn set-wavesurfer!
  [state plugins id]
  (swap! state assoc :wavesurfer (create-wavesurfer state plugins id)))

(defn set-loading-progress!
  [state progress]
  (swap! state assoc :loading-progress progress
         :loaded? (= progress 1)))

(defn raw-regions-map
  [^js wavesurfer]
  (js->clj (.. wavesurfer -regions -list)))

(defn ^js raw-region-by-id
  [id ^js wavesurfer]
  (some-> wavesurfer .-regions .-list (aget id)))

(defn regular-region->css-colour
  [region selected? {:regions/keys [colour-id->rgb colour-id-key]}]
  (let [colour-id (get region colour-id-key)
        rgb (colour-id->rgb colour-id region-default-rgb)
        alpha (region-selected->rgb-alpha selected?)]
    (rgb->css-colour rgb alpha)))

(defn time-marker->css-colour
  [{:keys [time-marker-type]} {:regions/keys [time-marker-types]}]
  (-> time-marker-type time-marker-types :rgb rgb->css-colour))

(defn css-index-of
  [m v]
  (str "_" (if-let [k (keys m)] (.indexOf k v) 0)))

(defn regular-region->attributes
  [region {:regions/keys [colour-id->rgb colour-id-key]}]
  (let [colour-id (get region colour-id-key)]
    #js{:colour-index (css-index-of colour-id->rgb colour-id)}))

(defn regular-region->raw-region
  [id {:keys [start end] :as region} {:regions/keys [selected-id selected-is-locked?] :as state}]
  (let [locked? (if (= id selected-id) selected-is-locked? true)]
    (clj->js
     {:id id
      :start start
      :end end
      :color (regular-region->css-colour region (= id selected-id) state)
      :drag (not locked?)
      :attributes (regular-region->attributes region state)})))

(defn time-marker->raw-region
  [id {:keys [time time-marker-type] :as region} {:keys [zoom-level] :regions/keys [time-marker-types] :as state}]
  (let [title (-> time-marker-type time-marker-types :label (str " marker"))]
    (clj->js
     {:id id
      :start time
      :end (+ time (time-marker-span-sec zoom-level))
      :color (time-marker->css-colour region state)
      :resize false
      :formatTimeCallback (constantly title)
      :attributes {:time-marker-type-index (css-index-of time-marker-types time-marker-type)}})))

(defn region->raw-region
  [id {:keys [time-marker-type] :as region} state]
  (if time-marker-type
    (time-marker->raw-region id region state)
    (regular-region->raw-region id region state)))

(defn set-regions!
  [{:regions/keys [live] :as state} ^js wavesurfer]
  (.clearRegions wavesurfer)
  (doseq [[k v] (sort-by (comp (juxt :time-marker-type :start) val) live)]
    (.addRegion wavesurfer (region->raw-region k v state))))

(defn set-regular-region-colours-and-attributes!
  [{:regions/keys [live selected-id] :as state} wavesurfer]
  (doseq [[id ^js raw-region] (raw-regions-map wavesurfer)
          :let [region (get live id)]
          :when (-> region :time-marker-type not)]
    (let [old (.-color raw-region)
          new (regular-region->css-colour region (= id selected-id) state)]
      (when (not= old new)
        (.update raw-region #js{:color new
                                :attributes (regular-region->attributes region state)})))))

(defn set-ready!
  [state]
  (swap! state assoc :ready? true))

(defn rewind
  [^js wavesurfer]
  (.seekAndCenter wavesurfer 0))

(defn seek-and-centre
  [^js wavesurfer time]
  (let [seek-to (/ time (.getDuration wavesurfer))]
    (when-not (infinite? seek-to)
      (.seekAndCenter wavesurfer seek-to))))

(defn pause
  [^js wavesurfer-or-region]
  (.pause wavesurfer-or-region))

(defn play
  [^js wavesurfer-or-region]
  (.play wavesurfer-or-region))

(defn play-pause
  [^js wavesurfer-or-region]
  (.playPause wavesurfer-or-region))

(defn set-playback-rate
  [^js wavesurfer value]
  (pause wavesurfer)
  (.setPlaybackRate wavesurfer
                    (parse-double value)))

(defn playing?
  [^js wavesurfer]
  (.isPlaying wavesurfer))

(defn playing-region?
  [^js wavesurfer ^js region]
  (and (playing? wavesurfer)
       (<= (.-start region) (.getCurrentTime wavesurfer) (.-end region))))

(def sort-regions-decreasingly-with-cache
  (let [cache (atom {:input nil :output nil})]
    (fn [regions-map]
      (let [{:keys [input output]} @cache]
        (if (= regions-map input)
          output
          (-> (reset! cache {:input regions-map
                             :output (sort-by (fn [[_ v]] (- (:start v))) regions-map)})
              :output))))))

(defn find-regular-region-by-time
  [regions-map time]
  (->> regions-map
       sort-regions-decreasingly-with-cache ; Using cache to generate less garbage as this is run very frequently (on audioprocess).
       (medley/find-first (fn [[_ v]] (and (not (:time-marker-type v))
                                           (<= (:start v) time (:end v)))))))

(defn find-time-marker-by-time-and-type
  [{:keys [zoom-level] :regions/keys [live]} time time-marker-type]
  (let [span-sec (time-marker-span-sec zoom-level)]
    (medley/find-first
     (fn [[_ v]] (and (= (:time-marker-type v) time-marker-type)
                      (<= (:time v) time (+ (:time v) span-sec))))
     live)))

(defn audio-state-change
  [{:keys [id on-audio-state-change] :as props} state wavesurfer]
  (let [loaded? (wavesurfer-loaded? state)
        playing? (playing? wavesurfer)]
    (when (not= playing? (:playing? @state))
      (swap! state assoc :playing? playing?)) ; This causes reagent-render&component-did-update on pause/play.

    (when on-audio-state-change ; TODO: Not used. Remove.
      (on-audio-state-change {:id id
                              :loaded? loaded?
                              :playing? playing?
                              :ready-state (if loaded? 4 0)}))))

(defn copy-keys-in-nested-maps
  [keyseq from to]
  (medley/map-kv-vals
   (fn [k v] (merge v (select-keys (get from k) keyseq)))
   to))

(defn regions-map
  [state wavesurfer]
  (let [{:regions/keys [live colour-id-key]} @state
        coerce-time-marker #(-> % (set/rename-keys {:start :time}) (dissoc :end))]
    (->> (raw-regions-map wavesurfer)
         (medley/map-vals (fn [^js r] {:start (.-start r) :end (.-end r)}))
         (copy-keys-in-nested-maps [colour-id-key :time-marker-type] live)
         (medley/map-vals #(cond-> % (:time-marker-type %) coerce-time-marker)))))

(defn update-state-on-regions-live-change
  [state regions]
  (assoc state :regions/live regions))

(defn update-state-on-regions-change
  [state regions]
  (-> state
      (assoc :regions/live regions
             :regions/stable regions
             :regions/redos [])
      (update :regions/undos conj (:regions/stable state))))

(defn update-state-on-undo
  [state undos redos]
  (if (empty? (undos state))
    state
    (let [new-regions (peek (undos state))]
      (-> state
          (assoc :regions/live new-regions
                 :regions/stable new-regions)
          (update undos pop)
          (update redos conj (:regions/live state))
          (cond-> (not (contains? new-regions (:regions/selected-id state)))
            (assoc :regions/selected-id nil))))))

(defn regular-region?
  [regions region-id]
  (-> regions (get region-id) :time-marker-type not))

(defn toggle-selected-region-lock!
  [state wavesurfer]
  (let [{:regions/keys [selected-id selected-is-locked? live]} @state
        locked? (not selected-is-locked?)]
    (when (regular-region? live selected-id)
      (when-let [region (some-> selected-id (raw-region-by-id wavesurfer))]
        (.update region #js{:drag (not locked?)})
        (swap! state assoc :regions/selected-is-locked? locked?)))))

(defn lock-unselected-region!
  [{:regions/keys [selected-id live] :as _old-state} wavesurfer]
  (when-let [region (some-> selected-id (raw-region-by-id wavesurfer))]
    (when (and (.-drag region)
               (regular-region? live selected-id))
      (.update region #js{:drag false}))))

(defn select-region!
  [state wavesurfer ^js region]
  (let [[old new] (swap-vals! state assoc
                              :regions/selected-id (some-> region .-id)
                              :regions/selected-is-locked? (some-> region .-drag not))]
    (when (not= (:regions/selected-id old)
                (:regions/selected-id new))
      (lock-unselected-region! old wavesurfer)
      (set-regular-region-colours-and-attributes! new wavesurfer))))

(defn select-regular-region-by-time!
  [state wavesurfer time]
  (let [region (some-> (:regions/live @state)
                       (find-regular-region-by-time time)
                       key
                       (raw-region-by-id wavesurfer))]
    (select-region! state wavesurfer region)))

(defn undo!
  [state wavesurfer undos redos]
  (let [[old new] (swap-vals! state update-state-on-undo undos redos)]
    (when (not= old new)
      (set-regions! new wavesurfer))))

(defn remove!
  [state wavesurfer]
  (when-let [^js region (get (raw-regions-map wavesurfer)
                             (:regions/selected-id @state))]
    (.remove region)
    (let [unselect #(assoc % :regions/selected-id nil)]
      (swap! state (comp unselect update-state-on-regions-change) (regions-map state wavesurfer)))))

(defn remove-old-temp-region
  [state wavesurfer]
  (let [regions (raw-regions-map wavesurfer)
        {:regions/keys [live colour-id-key]} @state
        temp-region-ids (->> regions
                             (keys)
                             (keep (fn [k] (when (and (string/starts-with? k "wavesurfer")
                                                      (not (get (get live k) colour-id-key)))
                                             k)))
                             (seq))
        _ (assert (or (nil? temp-region-ids) (= 1 (count temp-region-ids))))
        prev-temp-region (and temp-region-ids (get regions (first temp-region-ids)))]
    (when prev-temp-region (.remove prev-temp-region))))

(defn toggle-colour-id!
  [state wavesurfer colour-id]
  (let [{:regions/keys [selected-id live colour-id-key]} @state]
    (when selected-id
      (let [new-regions (-> (regions-map state wavesurfer)
                            (update-in [selected-id colour-id-key] #(when (not= % colour-id) colour-id)))]
        (when-not (get-in new-regions [selected-id colour-id-key])
          (remove-old-temp-region state wavesurfer))
        (when (not= new-regions live)
          (let [new-state (swap! state update-state-on-regions-change new-regions)]
            (set-regular-region-colours-and-attributes! new-state wavesurfer)))))))

(defn colour-selected?
  [state colour-id]
  (let [{:regions/keys [selected-id live colour-id-key]} @state]
    (= colour-id
       (get-in live [selected-id colour-id-key]))))

(defn find-the-other-colour-id
  "If we have two colour options and one selected in the given time, return the other colour."
  [state time]
  (let [{:regions/keys [live colour-id->rgb colour-id-key]} @state
        region (some-> (find-regular-region-by-time live time) val)
        current-colour-id (get region colour-id-key)
        all-colour-ids (set (keys colour-id->rgb))]
    (when (and current-colour-id
               (= 2 (count all-colour-ids)))
      (-> (set/difference all-colour-ids #{current-colour-id})
          first))))

(defn new!
  [state ^js wavesurfer]
  (let [time (.getCurrentTime wavesurfer)
        region (-> just-added-region-defaults
                   (assoc
                    :id (str (random-uuid))
                    :start time
                    :end (+ time new-region-span-sec))
                   clj->js)
        colour-id (find-the-other-colour-id state time)]
    (.addRegion wavesurfer region)
    (toggle-colour-id! state wavesurfer colour-id)))

(defn new-time-marker!
  [!state ^js wavesurfer time-marker-type]
  (let [{:regions/keys [live] :as state} @!state
        time (.getCurrentTime wavesurfer)
        region {:time time
                :time-marker-type time-marker-type}
        id (str (random-uuid))
        new-regions (assoc live id region)
        raw-region (time-marker->raw-region id region state)
        already-there (find-time-marker-by-time-and-type state time time-marker-type)]
    (when-not already-there
      (.addRegion wavesurfer raw-region)
      (swap! !state update-state-on-regions-change new-regions)
      (select-region! !state wavesurfer raw-region))))

(defn update-time-marker-widths
  [{:keys [zoom-level] :regions/keys [live]} wavesurfer]
  (let [span-sec (time-marker-span-sec zoom-level)]
    (doseq [[id ^js raw-region] (raw-regions-map wavesurfer)]
      (let [{:keys [time-marker-type time]} (get live id)]
        (when time-marker-type
          (.update raw-region #js{:end (+ time span-sec)}))))))

(def set-zoom-level*
  (goog.functions/debounce
   (fn [{:keys [zoom-level] :as state} ^js wavesurfer]
     (.zoom wavesurfer zoom-level)
     (update-time-marker-widths state wavesurfer))
   1))

(defn set-zoom-level!
  [state wavesurfer new-level]
  (let [new-state (swap! state assoc :zoom-level new-level)]
    (set-zoom-level* new-state wavesurfer)))

(defn on-destroy
  [{:keys [on-destroy-handler] :or {on-destroy-handler (constantly :no-op)} :as props} state wavesurfer]
  (on-destroy-handler (.getCurrentTime wavesurfer))
  (audio-state-change props state wavesurfer))

(defn on-event
  [^js wavesurfer event-name fn]
  (.on wavesurfer (name event-name) fn))

(defn on-ready
  [{:keys [on-ready-handler] :or {on-ready-handler (constantly :no-op)} :as _props} state ^js wavesurfer plugins]
  (on-ready-handler (.getDuration wavesurfer))
  (if (regions-plugin? plugins)
    (do
      (.initPlugin wavesurfer "regions")
      (set-ready! state)
      (.enableDragSelection wavesurfer (clj->js just-added-region-defaults)))
    (set-ready! state)))

(defn on-load
  [{:keys [on-load-handler] :or {on-load-handler (constantly :no-op)} :as props} state wavesurfer]
  (on-load-handler)
  (audio-state-change props state wavesurfer))

(defn on-error
  [{:keys [on-error-handler] :or {on-error-handler (constantly :no-op)} :as props} state wavesurfer]
  (on-error-handler)
  (audio-state-change props state wavesurfer))

(defn on-play
  [{:keys [on-play-handler] :or {on-play-handler (constantly :no-op)} :as props} state wavesurfer]
  (on-play-handler (.getCurrentTime wavesurfer))
  (audio-state-change props state wavesurfer))

(defn on-pause
  [{:keys [on-pause-handler] :or {on-pause-handler (constantly :no-op)} :as props} state wavesurfer]
  (on-pause-handler (.getCurrentTime wavesurfer))
  (audio-state-change props state wavesurfer))

(defn on-stop
  [props state wavesurfer]
  (audio-state-change props state wavesurfer))

(defn on-finish
  [{:keys [on-finish-handler]
    :or {on-finish-handler (constantly :no-op)}
    :as props}
   state wavesurfer]
  (on-finish-handler (.getCurrentTime wavesurfer))
  (rewind wavesurfer)
  (pause wavesurfer)
  (audio-state-change props state wavesurfer))

(defn on-seek
  [{:keys [on-playhead-move] :as props} state wavesurfer progress]
  (when on-playhead-move
    (on-playhead-move (.getCurrentTime wavesurfer)))
  (audio-state-change props state wavesurfer))

(defn on-audioprocess-fn
  [{:keys [on-playhead-move plugins] :as _props} state wavesurfer]
  (let [select-region (when (regions-plugin? plugins)
                        (goog.functions/throttle
                         (fn [time]
                           (when (playing? wavesurfer)
                             (select-regular-region-by-time! state wavesurfer time)))
                         50))
        trigger-event (goog.functions/throttle
                       (fn [time]
                         (when on-playhead-move
                           (on-playhead-move time)))
                       100)]
    (fn [time]
      (when select-region (select-region time))
      (trigger-event time))))

(defn on-region-click
  [state wavesurfer region e]
  (select-region! state wavesurfer region))

(defn on-region-contextmenu
  [state wavesurfer ^js region e]
  (let [{:regions/keys [live]} @state
        id (.-id region)]
    (when (regular-region? live id)
      (select-region! state wavesurfer region)
      (if (playing-region? wavesurfer region)
        (pause wavesurfer)
        (play region)))))

(defn on-region-created
  [state wavesurfer ^js region]
  (remove-old-temp-region state wavesurfer)
  (when (-> (.-data region) (js->clj :keywordize-keys true) :just-added?)
    (select-region! state wavesurfer region)))

(defn on-region-updated
  [state wavesurfer ^js region]
  (when (or (.-isDragging region)
            (.-isResizing region))
    (select-region! state wavesurfer region))
  (swap! state update-state-on-regions-live-change (regions-map state wavesurfer)))

(defn on-region-update-end
  [state wavesurfer region]
  (swap! state update-state-on-regions-change (regions-map state wavesurfer)))

(defn on-key-down
  [state wavesurfer ^js e]
  (when (contains? #{"Delete" "Backspace"} (.-key e))
    (remove! state wavesurfer)))

(defn setup-on-regions-change-events
  [state on-regions-change on-time-markers-change]
  (let [on-regions-change (or on-regions-change (constantly :no-op))
        on-time-markers-change (or on-time-markers-change (constantly :no-op))
        regular-selected-id (fn [{:regions/keys [selected-id live]}]
                              (when (regular-region? live selected-id)
                                selected-id))
        regular-regions (fn [{:regions/keys [live]}]
                          (medley/remove-vals :time-marker-type live))
        time-markers (fn [{:regions/keys [live]}]
                       (medley/filter-vals :time-marker-type live))]
    (add-watch
     state
     :regions/on-change
     (fn [_ _ old new]
       (when-let [event (merge
                         (when (not= (regular-selected-id old)
                                     (regular-selected-id new))
                           {:old-selected-region-id (regular-selected-id old)
                            :new-selected-region-id (regular-selected-id new)})
                         (when (not= (regular-regions old)
                                     (regular-regions new))
                           {:old-regions (regular-regions old)
                            :new-regions (regular-regions new)}))]
         (on-regions-change event))
       (when (not= (time-markers old)
                   (time-markers new))
         (on-time-markers-change (time-markers new)))))))

(defn watch-regions-state-for-debugging
  [state]
  (add-watch state :debug (fn [_ _ o n]
                            (let [ks [:regions/live :regions/stable :regions/undos :regions/redos :regions/selected-id]]
                              (when (not= (select-keys o ks) (select-keys n ks))
                                (js/console.log (select-keys n ks)))))))

(defn reload-regions
  [state wavesurfer new-regions live on-regions-change on-time-markers-change]
  (when (not= new-regions live)
    (remove-watch state :regions/on-change)
    (try
      (let [new-state (swap! state update-state-on-regions-change new-regions)]
        (set-regions! new-state wavesurfer))
      (finally
        (setup-on-regions-change-events state on-regions-change on-time-markers-change)))))

(defn initialise-regions
  [state wavesurfer new-regions live]
  (let [new-state (swap! state assoc
                         :regions/live new-regions
                         :regions/stable new-regions
                         :regions/initialised-from-props? true)]
    (when (not= new-regions live)
      (set-regions! new-state wavesurfer))))

(defn load-regions-from-props!
  [state wavesurfer {:keys [reload-regions? on-regions-change on-time-markers-change] :as props}]
  (let [new-regions (merge (:regions props) (:time-markers props))
        {:regions/keys [live stable initialised-from-props?] :keys [ready?]} @state
        user-is-updating-region? (not= live stable)]
    (cond
      reload-regions?
      (reload-regions state wavesurfer new-regions live on-regions-change on-time-markers-change)

      (or initialised-from-props? (not ready?))
      nil

      user-is-updating-region?
      (swap! state assoc :regions/initialised-from-props? true)

      (seq new-regions)
      (initialise-regions state wavesurfer new-regions live))))

(defn select-region-form-props!
  [state wavesurfer {:keys [region-id-to-select] :as props}]
  (let [{:keys [ready?] :regions/keys [last-id-to-select]} @state]
    (when (and ready?
               (not= last-id-to-select region-id-to-select))
      (swap! state assoc :regions/last-id-to-select region-id-to-select)
      (when region-id-to-select
        (let [region (raw-region-by-id region-id-to-select wavesurfer)]
          (select-region! state wavesurfer region))))))

(defn initialise-playhead-position!
  [state wavesurfer {:keys [playhead-position] :as props}]
  (let [{:keys [ready? playhead-position-initialised?]} @state]
    (when (and ready?
               (not playhead-position-initialised?))
      (swap! state assoc :playhead-position-initialised? true)
      (when playhead-position
        (seek-and-centre wavesurfer playhead-position)
        (select-regular-region-by-time! state wavesurfer playhead-position)))))

(defn icon-button
  [{:keys [on-click icon-name title appearance]}]
  [button {:appearance #{:invisible :tool-btn}
           :on-click on-click}
   [icon {:class (conj appearance :audio-wave-player__control-icon)
          :icon icon-name
          :title title
          :size 18}]])

(defn text-button
  [{:keys [on-click colour text title selected? solid?]}]
  [button {:appearance #{:invisible :tool-btn}
           :class (cond-> [] solid? (conj :solid-text-tool-btn) selected? (conj :selected))
           :on-click on-click}
   [:div
    {:style {(if solid? :background-color :color) colour}
     :title (or title text)}
    text]])

(defn zoom-slider
  [{:keys [state wavesurfer]}]
  [:input.zoom-slider
   {:on-change (fn [^js e] (set-zoom-level! state wavesurfer (.. e -target -valueAsNumber)))
    :min 10
    :max 100
    :type :range
    :value (:zoom-level @state)
    :title (str "Zoom: " (:zoom-level @state) "%")}])

(defcomp wave-player
  {:component-type ::wave-player
   :theme? true}
  [{:keys [allow-playback-rate-change? appearance audio-src auth-token component-did-mount component-will-unmount id options other-media-playing? plugins on-regions-change on-time-markers-change theme colour-palette colour-id-key time-marker-types]
    :or {other-media-playing? (constantly false)}
    :as props}]
  (let [current-theme (apply merge ((apply juxt appearance) theme))
        options (-> options
                    (update :wavesurfer assoc
                            :cursorColor (:cursor-colour current-theme)
                            :height (:height current-theme)
                            :progressColor (:progress-peaks-colour current-theme)
                            :waveColor (:wave-peaks-colour current-theme))
                    (update :wavesurfer deep-merge {:xhr
                                                    {:requestHeaders
                                                     [{:key "Authorization"
                                                       :value auth-token}]}}))
        state (-> initial-state
                  (update :options deep-merge options) ; TODO: Why do we store options in an atom?
                  (assoc :regions/colour-id->rgb (->> colour-palette (map (juxt :value :rgb)) (into {})))
                  (assoc :regions/colour-id-key colour-id-key)
                  (assoc :regions/time-marker-types (->> time-marker-types (map (juxt :value #(select-keys % [:rgb :label]))) (into {})))
                  (reagent/atom))
        dom-node (atom nil)]
    ;(watch-regions-state-for-debugging state)
    (when (regions-plugin? (:plugins props))
      (setup-on-regions-change-events state on-regions-change on-time-markers-change))
    (reagent/create-class
     {:display-name "audio-player"
      :component-did-mount
      (fn [this]
        (set-options! state options)
        (set-wavesurfer! state plugins id) ; TODO: Maybe just create it in a (delay) in the top let?
        (let [^js wavesurfer (get-wavesurfer state)]
          (.init wavesurfer)
          (when component-did-mount
            (component-did-mount wavesurfer))
          (on-event wavesurfer :destroy #(on-destroy props state wavesurfer))
          (on-event wavesurfer :finish #(on-finish props state wavesurfer))
          (on-event wavesurfer :load #(on-load props state wavesurfer))
          (on-event wavesurfer :loading (partial set-loading-progress! state))
          (on-event wavesurfer :pause #(on-pause props state wavesurfer))
          (on-event wavesurfer :play #(on-play props state wavesurfer))
          (on-event wavesurfer :ready #(on-ready props state wavesurfer plugins))
          (on-event wavesurfer :error #(on-error props state wavesurfer))
          (on-event wavesurfer :seek (partial on-seek props state wavesurfer))
          (on-event wavesurfer :stop #(on-stop props state wavesurfer))
          (on-event wavesurfer :audioprocess (on-audioprocess-fn props state wavesurfer))
          (when (regions-plugin? (:plugins props))
            (on-event wavesurfer :region-click (partial on-region-click state wavesurfer))
            (on-event wavesurfer :region-contextmenu (partial on-region-contextmenu state wavesurfer))
            (on-event wavesurfer :region-created (partial on-region-created state wavesurfer))
            (on-event wavesurfer :region-updated (partial on-region-updated state wavesurfer))
            (on-event wavesurfer :region-update-end (partial on-region-update-end state wavesurfer))
            (gevents/listen @dom-node "contextmenu" event/stop)
            (gevents/listen @dom-node "keydown" (partial on-key-down state wavesurfer)))
          (.load wavesurfer audio-src)))

      :component-did-update
      (fn [this old-argv]
        (let [props (reagent/props this)
              ^js wavesurfer (get-wavesurfer state)
              ready? @state]
          (.drawBuffer wavesurfer)
          (let [new-audio-src (-> this reagent/argv second :audio-src)
                old-audio-src (-> old-argv second :audio-src)]
            (when (not= new-audio-src old-audio-src)
              (when (regions-plugin? (:plugins props))
                (throw (ex-info "Updating audio not allowed when using regions" {:new new-audio-src :old old-audio-src})))
              (.load wavesurfer new-audio-src)))
          (when (regions-plugin? (:plugins props))
            (load-regions-from-props! state wavesurfer props)
            (select-region-form-props! state wavesurfer props)
            (initialise-playhead-position! state wavesurfer props))
          (when (and ready? (playing? wavesurfer) (other-media-playing? props)) ; TODO: other-media-playing? not used, remove.
            (pause wavesurfer))))

      :component-will-unmount
      (fn []
        (let [^js wavesurfer (get-wavesurfer state)]
          (when component-will-unmount
            (component-will-unmount wavesurfer))
          (.destroy wavesurfer)))

      :reagent-render
      (fn [{:keys [actions allow-playback-rate-change? appearance id title title-prefix colour-palette time-marker-types] :as props}]
        (let [{:keys [loading-progress ready?] :regions/keys [selected-id selected-is-locked?]} @state
              locked? (or selected-is-locked? (not selected-id))
              wavesurfer (get-wavesurfer state)
              all-actions (cond-> actions
                            (regions-plugin? (:plugins props))
                            (concat
                             (for [{:keys [value label rgb]} colour-palette
                                   :let [selected? (colour-selected? state value)
                                         alpha (region-selected->rgb-alpha selected?)]]
                               [text-button {:on-click #(toggle-colour-id! state wavesurfer value) :colour (rgb->css-colour rgb alpha) :text label :selected? selected? :solid? true}])
                             [[separator]
                              [icon-button {:on-click #(undo! state wavesurfer :regions/undos :regions/redos) :icon-name :undo :title "Undo" :appearance appearance}]
                              [icon-button {:on-click #(undo! state wavesurfer :regions/redos :regions/undos) :icon-name :redo :title "Redo" :appearance appearance}]
                              [icon-button {:on-click #(new! state wavesurfer) :icon-name :document-add :title "New" :appearance appearance}]
                              [icon-button {:on-click #(remove! state wavesurfer) :icon-name :bin :title "Remove" :appearance appearance}]
                              [icon-button {:on-click #(toggle-selected-region-lock! state wavesurfer) :icon-name (if locked? :lock :unlocked) :title (if locked? "Unlock movement" "Movement unlocked") :appearance appearance}]
                              [separator]]
                             (for [{:keys [value label rgb]} time-marker-types]
                               [text-button {:on-click #(new-time-marker! state wavesurfer value) :colour (rgb->css-colour rgb) :text label :title (str "New " label " marker")}])
                             [[separator]
                              [zoom-slider {:state state :wavesurfer wavesurfer}]]))]

          [:div.audio-wave-player (merge {:class appearance}
                                         (when (regions-plugin? (:plugins props))
                                           {:tab-index "0"
                                            :ref (partial reset! dom-node)}))

           [layout {:class (conj appearance :audio-wave-player__controls)}

            [:div.audio-wave-player__play-button
             (if ready?
               [:div
                [button {:appearance #{:invisible}
                         :id :audio-wave-player__play-button
                         :on-click #(play-pause wavesurfer)}
                 [icon {:class (conj appearance :audio-wave-player__control-icon)
                        :icon (if (playing? wavesurfer) :pause :play)
                        :size 40}]]
                (if allow-playback-rate-change?
                  [:div
                   [:select {:on-change #(set-playback-rate wavesurfer
                                                            (.-value (.-target %)))}
                    [:option {:value "1.0"}
                     "Normal"]
                    [:option {:value "0.75"}
                     "Slow"]
                    [:option {:value "0.5"}
                     "Slowest"]]]
                  [:span])]
               [spinner {:class (conj appearance :audio-wave-player__control-icon)
                         :size 40}])]

            (let [titles-class (cond-> #{:audio-wave-player__titles}
                                 (nil? title-prefix) (conj :no-title-prefix))]
              (cond-> [:div {:class titles-class}]
                title-prefix (conj [:p.audio-wave-player__title-prefix title-prefix])
                :always (conj (common/update-properties title update :class #(conj % :audio-wave-player-title)))))

            (when all-actions
              [:div.audio-wave-player__actions
               (into [:div.audio-wave-player__actions-inner] all-actions)])]

           [panel {:appearance #{:primary :no-padding}}
            [layout {:appearance #{:centered}}
             [:div.audio-wave-player__wavesurfer {:id (make-container-id id)}]
             (when-not ready?
               [:progress.audio-wave-player__progress-bar
                {:class appearance
                 :value (if (< loading-progress 100) loading-progress nil)
                 :max 100}])]]]))})))
